import { Autocomplete, Checkbox, TextField } from "@vapor/react-material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getIndexDocument,
    postIndexDocument,
} from "../../actions/indexDocument";
import {
    DocLabelUpdateAction,
    GlobalLabelUpdateAction,
    updateDocLabels,
    updateGlobalLabels,
} from "../../actions/labels";
import { getAllOrders } from "../../actions/orders";
import { getProducts } from "../../actions/products";
import { OrderLabel } from "../../interfaces/label";
import { OrderInterface } from "../../interfaces/orders";
import { exportOrdersToCSV } from "../../services/csv-export";
import OrderCard from "../general/cards/order";
import { ManageLabelsDialog } from "../general/labels/manageLabelDialog";
import Button from "../vapor/button";
import Input from "../vapor/input";
import Item from "../vapor/item";
import Spinner from "../vapor/spinner";
import "./style.scss";

declare const window: any;

interface OrdersFiltersProps {
    onChange: (params: any) => void;
}

const DocumentsComponent = (props: {}) => {
    const dispatch: any = useDispatch();

    // Redux States
    const orders = useSelector((state: RootState) => state.search.orders);
    const products = useSelector((state: RootState) => state.products);
    const indexDocument = useSelector(
        (state: RootState) => state.indexDocument,
    );
    // Memo (derived from indexDocument)
    const availableLabels = useMemo<Array<string>>(() => {
        return indexDocument?.data?.labels
            ? Object.keys(indexDocument.data.labels)
            : [];
    }, [indexDocument]);

    const indexLabels: Array<OrderLabel> = useMemo(() => {
        return availableLabels
            ? availableLabels.map((key) => {
                  return {
                      name: key,
                      color: indexDocument.data.labels[key].color,
                  };
              })
            : [];
    }, [availableLabels, indexDocument?.data?.labels]);

    const labelColorMapping = useMemo(() => {
        return indexLabels
            ? Object.fromEntries(
                  indexLabels.map(({ name, color }) => [
                      name.toLowerCase(),
                      color,
                  ]),
              )
            : {};
    }, [indexLabels]);

    // Transform mapping label => docIds to docId => labels
    const labelsByOrderId = useMemo(() => {
        let _labelsByOrderId: Record<string, Array<string>> = {};
        for (let labelName in indexDocument?.data?.labels) {
            const docIds = indexDocument.data.labels[labelName]
                .docIds as Array<string>;
            docIds.forEach((docId: string) => {
                if (!Object.hasOwn(_labelsByOrderId, docId)) {
                    _labelsByOrderId[docId] = [];
                }
                _labelsByOrderId[docId].push(labelName);
            });
        }
        return _labelsByOrderId;
    }, [indexDocument]);

    const usedLabels = useMemo(() => {
        const labelsData = indexDocument?.data?.labels || [];
        let res: string[] = [];
        availableLabels.forEach((labKey) => {
            const lab = labelsData[labKey];
            if (lab.docIds && lab.docIds.length !== 0) {
                res.push(labKey);
            }
        });

        return res;
    }, [availableLabels, indexDocument]);

    // Local States
    const [ordersToShow, setOrdersToShow] = useState([]);
    const [parameters, setParameters] = useState({
        order: "new",
        search: "",
        labels: [] as Array<string>,
        companySearch: "",
        period: null,
    });

    const productNameMapping = products.data.reduce(
        (acc: any, product: any) => {
            acc[product.product] = product.name || product.product;
            return acc;
        },
        {},
    );

    useEffect(() => {
        dispatch(getProducts());
        dispatch(getAllOrders());
        dispatch(getIndexDocument());
        window?.aptrinsic?.("track", "business-information:DOCUMENTS-LIST");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let ordersC: any = [];
        orders.data.map((order: OrderInterface) => {
            if (parameters.search) {
                if (
                    (!order.name ||
                        order.name
                            ?.toLowerCase()
                            .indexOf(parameters.search.toLowerCase()) === -1) &&
                    products.data
                        .find((product) => product.product === order.product)
                        ?.name.toLowerCase()
                        .indexOf(parameters.search.toLowerCase()) === -1
                ) {
                    return null;
                }
            }

            if (parameters.labels.length > 0) {
                let orderLabels: Array<string> | undefined = undefined;
                if (Object.hasOwn(labelsByOrderId, order.order_id)) {
                    orderLabels = labelsByOrderId[order.order_id];
                }

                if (
                    !orderLabels ||
                    orderLabels === undefined ||
                    orderLabels.length === 0 ||
                    !parameters.labels.some((label) =>
                        (orderLabels || []).includes(label),
                    )
                ) {
                    return null;
                }
            }

            if (
                !products.data.find(
                    (product) => product.product === order.product,
                )
            ) {
                return null;
            }
            if (
                products.data
                    .find((product) => product.product === order.product)
                    ?.formats?.indexOf("PDF") === -1 &&
                products.data
                    .find((product) => product.product === order.product)
                    ?.formats?.indexOf("XBRL") === -1
            ) {
                return null;
            }
            return ordersC.push(order);
        });
        setOrdersToShow(ordersC);
    }, [
        orders,
        parameters.search,
        parameters.labels,
        products.data,
        labelsByOrderId,
    ]);

    const onFiltersChange = (kind: string, val: string | Array<string>) => {
        setParameters({ ...parameters, [kind]: val });
    };

    const OrdersFilters = (props: OrdersFiltersProps) => {
        return (
            <div className="bis-orders-filters-root">
                <Item width="100%" label="Cerca">
                    <Input
                        name={"bis-orders-filters-search"}
                        value={parameters.search}
                        onChange={(val) =>
                            setParameters({ ...parameters, search: val })
                        }
                        onSearch={(val?: string) =>
                            onFiltersChange("search", val || "")
                        }
                        showSearch
                        placeholder="Inserisci azienda o tipo documento"
                    />
                </Item>
                <Item>
                    <Autocomplete
                        multiple
                        id="checkboxes-tags"
                        options={(indexLabels && indexLabels.length
                            ? indexLabels
                            : []
                        ).map((option) => ({
                            title: option.name,
                        }))}
                        disableCloseOnSelect
                        getOptionLabel={(option: any) =>
                            typeof option === "string" ? option : option.title
                        }
                        renderOption={(props, option: any, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {typeof option === "string"
                                    ? option
                                    : option.title}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Filtra per etichette"
                            />
                        )}
                        onChange={(event, value: any) => {
                            // value can be an array of option objects or strings.
                            const labels =
                                value?.map((v: any) => v.title) || [];
                            onFiltersChange("labels", labels);
                        }}
                        value={parameters.labels.map((label) => ({
                            title: label,
                        }))}
                        isOptionEqualToValue={(option: any, value: any) =>
                            option.title === value.title
                        }
                    />
                </Item>
                <Item>
                    <div className="bis-orders-export">
                        <ManageLabelsDialog
                            usedLabels={usedLabels}
                            labels={indexLabels}
                            onSave={(
                                updateActions: Array<GlobalLabelUpdateAction>,
                            ) => {
                                const newIndexDocumentLabels =
                                    updateGlobalLabels(
                                        indexDocument.data.labels,
                                        updateActions,
                                    );
                                dispatch(
                                    postIndexDocument({
                                        ...indexDocument.data,
                                        labels: newIndexDocumentLabels,
                                    }),
                                );
                                if (updateActions.length > 0) {
                                    // Reset label filters
                                    onFiltersChange("labels", []);
                                }
                            }}
                        />
                        <Button
                            size="medium"
                            disabled={ordersToShow.length === 0}
                            onClick={() => {
                                if (ordersToShow.length === 0) return;
                                exportOrdersToCSV(
                                    ordersToShow,
                                    productNameMapping,
                                    labelsByOrderId,
                                    "orders_export",
                                );
                            }}
                        >
                            Esporta CSV
                        </Button>
                    </div>
                </Item>

                {/*<Item width="20%" label="Seleziona periodo"><Input name={'bis-orders-filters-period'} /></Item>*/}
            </div>
        );
    };

    return (
        <div className="bis-orders-root">
            {<OrdersFilters onChange={() => {}} />}
            {/** move here button */}
            {(orders.isGettingOrders || products.isGettingProducts) && (
                <div className="bis-orders-spinner">
                    <Spinner />
                </div>
            )}
            {!orders.isGettingOrders && !products.isGettingProducts && (
                <div className="bis-orders-results">
                    {ordersToShow.map((order: OrderInterface) => {
                        const productFind = products.data.find(
                            (product) => product.product === order.product,
                        );
                        if (
                            productFind &&
                            productFind.formats.length === 1 &&
                            productFind.formats[0].toUpperCase() === "JSON"
                        ) {
                            return null;
                        }
                        return (
                            <OrderCard
                                key={order.order_id}
                                item={order}
                                orderLabels={labelsByOrderId[order.order_id]}
                                labelColorsMapping={labelColorMapping || {}}
                                product={productFind}
                                availableLabels={indexLabels}
                                onSelect={() => {}}
                                onLabelsUpdate={(
                                    docLabelActions: Array<DocLabelUpdateAction>,
                                ) => {
                                    console.log(
                                        "Updating order labels for order:",
                                        order.order_id,
                                        "with actions:",
                                        docLabelActions,
                                    );
                                    const newIndexDocumentLabels =
                                        updateDocLabels(
                                            indexDocument.data.labels,
                                            {
                                                docId: order.order_id,
                                                actions: docLabelActions,
                                            },
                                        );
                                    dispatch(
                                        postIndexDocument({
                                            ...indexDocument.data,
                                            labels: newIndexDocumentLabels,
                                        }),
                                    );
                                }}
                            />
                        );
                    })}
                </div>
            )}
            {/*<div style={{ position: "fixed", right: "32px", top: "16px" }}>
                <UploadStatusIndicator allUploaded={allUploaded} />
            </div>*/}
        </div>
    );
};

export default DocumentsComponent;
